import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Alert, Container, FormGroup,
    Spinner,
} from 'reactstrap';
import cls from 'classnames';
import moment from 'moment';
import {
    faCheckCircle,
    faToolbox,
    faTruck,
    faCommentDots,
    faComments,
    faTimesCircle,
    faPhoneSquareAlt,
    faUser,
    faCircleNotch,
    faHardHat,
    faUserClock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@s20/components/common/Link';
import { crewLeadLabel } from '@s20/i18n';
import { appendDispatchTimeZone } from '@s20/common/util/date';
import { MomentTimeFormat } from '@s20/common/constants/momentFormats';
import { getGroupSmsUrl, getMapUrl } from '@s20/common/util/dom';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import {
    UncontrolledCollapseUnderlineHeader,
    FlexBetweenRow,
    FlexCenterRow,
    FlexColumnStart,
    FlexStartRow,
    FormLabel,
    StyledButton,
    toasty,
    FormCheckbox,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,

} from '../Common';
import {
    ContractTimesheetResponsibilityType,
    TimesheetResponsibilityType,
} from '../tenant/Tenant';
import authService from '../api-authorization/AuthorizeService';
import { JobDetails } from '../job/Job';
import { CountyLabel } from '../uscounties/CountyLabel';
import FileList from '../common/forms/FileList';
// import { CallOffButton } from '../callOff/CallOffButton';
import './AssignmentConfirmation.scss';

export const ConfirmationStatus = {
    Unfilled: 1,
    Scheduled: 2,
    NoResponse: 3,
    Confirmed: 4,
    CancelPending: 5,
    ThresholdCancel: 6,
};

export const JobConfirmationStatusOptions = [
    { value: 1, class: 'unfilled', label: 'Unfilled' },
    { value: 2, class: 'scheduled', label: 'Scheduled' },
    { value: 3, class: 'noResponse', label: 'No Response' },
    { value: 4, class: 'confirmed', label: 'Confirmed' },
    { value: 5, class: 'cancelPending', label: 'Cancel Pending' },
];

class AssignmentConfirmation extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            assignment: null,
            crewAndEquipOpen: true,
            invalidChargeTypeIds: [],
            loading: true,
            saving: false,
            notFound: false,
            timesheetOpen: true,
            timesheetValid: false,
            timesheetValidated: false,
            enforceCrewLeadTimesheet: true,
            isDisclaimerCheckedFromBackend: false,
            hasAgreedToDriverDisclaimer: false, // Track checkbox state
            disclaimerError: false,
            disclaimerDisabled: false,
            usCounties: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    // Utility function  if the assigned employee is linked to relevant equipment
    requiresDriverDisclaimer = (assignment) => {
        if (!this.context.tenant.tenantSettings.enableDriverRideConfirmationFields) {
            return false;
        }

        const { assignedEmployeeId, equipment } = assignment;
        const relevantTypes = ['truck', 'van', 'tma'];

        return equipment.some((item) => {
            const equipmentType = item.assignedEquipmentType?.toLowerCase() || '';
            return (
                item.equipmentEmployeeAssignmentId === assignedEmployeeId &&
                relevantTypes.some((type) => equipmentType.includes(type))
            );
        });
    };

    onDisclaimerChange = (e) => {
        if (!this.state.disclaimerDisabled) {
            this.setState({
                hasAgreedToDriverDisclaimer: e.target.checked,
                disclaimerError: false,
            });
        }
    };

    confirmAssignment = async () => {
        const {
            assignment, enforceCrewLeadTimesheet, hasAgreedToDriverDisclaimer,
        } = this.state;
        const { t } = this.props;

        const showDriverDisclaimer = this.requiresDriverDisclaimer(assignment);

        if (!hasAgreedToDriverDisclaimer && showDriverDisclaimer) {
            this.setState({ disclaimerError: true });
            toasty.error(t('confirm:driverDisclaimerRequired'));
            return; // Prevent submission
        }

        this.setState({ saving: true });

        const payload = {
            notes: null,
            hasAgreedToDriverDisclaimer: showDriverDisclaimer ? hasAgreedToDriverDisclaimer : false,
        };

        const timesheetId = await httpClient.post(
            ApiRoutes.jobAssignment.confirm(assignment.jobAssignmentId),
            payload,
        );

        this.setState({ saving: false });

        const timesheetUrl = `${AppNavPaths.Timesheet}/${timesheetId}/${assignment.jobAssignmentId}`;

        /**
         * If we are enforcing crew leads as the timesheet masters, then make sure the user is a crew lead.
         * Otherwise, send them to their assignment calendar.
         * */
        if (enforceCrewLeadTimesheet) {
            this.context.history.push(
                assignment.isCrewLead
                    ? timesheetUrl
                    : AppNavPaths.AssignmentCalendar,
            );
        } else {
            this.context.history.push(timesheetUrl);
        };
    };

    confirmCancelledAssignment = async () => {
        const { assignment } = { ...this.state };
        this.setState({ saving: true });
        const timesheetId = await httpClient.put(
            ApiRoutes.jobAssignment.cancelConfirmed(assignment.jobAssignmentId),
        );

        this.setState({ saving: false });

        if (timesheetId !== 0) this.context.history.push(`${AppNavPaths.Timesheet}/${timesheetId}/${assignment.jobAssignmentId}`);
        else this.context.history.push(`${AppNavPaths.AssignmentCalendar}`);
    };

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const assignmentId = this.props.match.params.id;

            if (!assignmentId) {
                this.setState({ notFound: true, loading: false });
            } else {
                const response = await httpClient.get(
                    ApiRoutes.jobAssignment.byId(assignmentId),
                    httpClient.format.none,
                );
                if (response.ok) {
                    const assignment = await response.json();

                    // Check for hasAgreedToDriverDisclaimer
                    const hasAgreedToDriverDisclaimer =
                        assignment.crew?.some((member) => member.hasAgreedToDriverDisclaimer) || false;


                    if (!assignment) {
                        this.setState({ notFound: true, loading: false });
                    } else if (this.context.tenant.tenantSettings) {
                        /**
                         *  Determine who fills out the timesheets.  THis will be used to determine where to direct the user after
                         *  assignment confirmation.
                         * */
                        const enforceCrewLeadTS =
                            // Are we set for crew lead timesheets at the tenant level?
                            this.context.tenant.tenantSettings
                                .timesheetResponsibilityType
                            === TimesheetResponsibilityType.CrewLead
                            // Are we set for timesheets per contract, and contract is set up for crew lead?
                            || (this.context.tenant.tenantSettings
                                .timesheetResponsibilityType
                                === TimesheetResponsibilityType.Contract
                                && assignment.contractTimesheetResponsibilityTypeId
                                === ContractTimesheetResponsibilityType.CrewLead);

                        const usCounties = await httpClient.js(ApiRoutes.USCounties.all());

                        this.setState({
                            assignment,
                            hasAgreedToDriverDisclaimer,
                            disclaimerError: false,
                            disclaimerDisabled: hasAgreedToDriverDisclaimer, // Disable checkbox if already agreed
                            loading: false,
                            enforceCrewLeadTimesheet: enforceCrewLeadTS,
                            isDisclaimerCheckedFromBackend: hasAgreedToDriverDisclaimer,
                            usCounties,
                        });
                    }
                } else if (response.status === httpClient.statusCodes.unauthorized) {
                    this.context.history.push(
                        AppNavPaths.Unauthorized,
                    );
                } else {
                    toasty.error(this.props.t('confirm:loadFailed'));
                }
            }
        }
    };

    validateTimesheet = () => {
        const { assignment } = this.state;
        const timeFormat = 'HH:mm a';
        const invalidChargeTypeIds = [];

        for (const a of assignment.timesheet.assignments) {
            const valid = !!a.notApplicable
                // Start
                || (!!a.startTime
                    && !!moment(a.startTime, timeFormat, true).isValid())
                // end
                || !!a.endTime
                || !!moment(a.endTime, timeFormat, true).isValid();
            if (!valid) invalidChargeTypeIds.push(a.id);
        }

        this.setState({
            timesheetValidated: true,
            timesheetValid:
                !invalidChargeTypeIds.length
                && !!assignment.timesheet.workOrderNumber,
            invalidChargeTypeIds,
        });
    };

    render() {
        const {
            assignment,
            loading,
            saving,
            notFound,
            usCounties,
        } = { ...this.state };
        const { t } = this.props;

        const { tenantSettings } = { ...this.context.tenant };

        const jobDetailsHeader = tenantSettings
            && tenantSettings.showContractDescriptionInTimesheetView
            && assignment
            && assignment.jobName
            ? t('confirm:jobDetailsWithName', { name: assignment.jobName })
            : t('confirm:jobDetails');

        const confirmed = (assignment ?? {}).confirmationStatusId
            === ConfirmationStatus.Confirmed;
        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        if (loading || !(this.context.tenant ?? {}).tenantSettings) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }
        const { jobUploadsEnabled } = {
            ...this.context.tenant.tenantSettings,
        };

        const allEquipment = assignment.equipment ?? [];

        // What equipment does the confirming employee own of this list?
        const ownedEquipment = allEquipment.filter(
            (x) => x.equipmentEmployeeAssignmentId
                === assignment.assignedEmployeeId,
        );
        const ownedEquipmentIds = ownedEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        // Of the equipment, which have been assigned to any of the equipment owned by the confirming employee?
        const dependentEquipment = allEquipment.filter((x) => ownedEquipmentIds.includes(x.equipmentAssignedEquipmentId));
        const dependentEquipmentIds = dependentEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        const showDriverDisclaimer = this.requiresDriverDisclaimer(assignment);
        const jobStartTime = appendDispatchTimeZone(
            assignment?.jobStartTime ?? '',
            assignment?.jobStartTimeOffset,
            assignment?.timeZone,
            assignment?.dispatchTimeZone,
            MomentTimeFormat.AnteTwelveHour,
        );

        const county = getCounty(usCounties, {
            usCountyId: assignment.usCounty?.id,
            contractUnionWorkTypeId: assignment.contractUnionWorkTypeId,
        });

        return (
            <div className="confirmationContainer pt-2">
                <Container size="sm">
                    {!!notFound && (
                        <h4 className="text-center">
                            {t('confirm:notFound')}
                        </h4>
                    )}
                    {!!assignment && (
                        <>
                            {!!confirmed && (
                                <Alert
                                    className="mb-3 p-1 text-center"
                                    color="success"
                                >
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        size="lg"
                                        className="mr-2"
                                    />
                                    {t('confirm:alertConfirmed')}
                                </Alert>
                            )}

                            <UncontrolledCollapseUnderlineHeader
                                headerText={jobDetailsHeader}
                                defaultIsOpen
                            >
                                {(this.state.assignment ?? {})
                                    .confirmationStatusId
                                    === ConfirmationStatus.CancelPending && (
                                        <Alert
                                            className="mb-3 p-1 text-center"
                                            color="danger"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                size="lg"
                                                className="mr-2"
                                            />
                                            {t('confirm:alertCancelled')}
                                        </Alert>
                                    )}
                                {(this.state.assignment ?? {})
                                    .confirmationStatusId
                                    === ConfirmationStatus.ThresholdCancel && (
                                        <Alert
                                            className="mb-3 p-1 text-center"
                                            color="danger"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                size="lg"
                                                className="mr-2"
                                            />
                                            {t('confirm:alertCancelledByCustomer')}
                                        </Alert>
                                    )}
                                <JobDetails
                                    customerName={
                                        assignment?.customerName
                                    }
                                    subcontractorName={
                                        assignment?.subcontractorName
                                    }
                                    foremanName={
                                        assignment?.foremanName
                                    }
                                    foremanPhone={
                                        assignment?.foremanPhone
                                    }
                                    jobId={assignment?.jobId}
                                    projectId={assignment?.projectId}
                                    jobNotes={assignment?.jobNotes}
                                    jobNumber={assignment?.jobNumber}
                                    jobStartTime={
                                        jobStartTime
                                    }
                                    workOrderNumber={
                                        assignment?.workOrderNumber
                                    }
                                    tenantSettings={tenantSettings}
                                    jobName={assignment.jobName}
                                />
                                <div className="locationDetails">
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="meetingLocation"
                                            label={t('confirm:meetingLocation')}
                                            className="bottom-border"
                                        />
                                        <FlexStartRow>
                                            {!!(assignment ?? {})
                                                .meetingLocationUrlFormatted && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(assignment.meetingLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                            <span
                                                id="meetingLocation"
                                                name="meetingLocation"
                                            >
                                                {assignment.meetingLocation
                                                    ?? '-'}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="jobLocation"
                                            label={t('confirm:jobLocation')}
                                            className="bottom-border"
                                        />
                                        <FlexStartRow>
                                            {!!(assignment ?? {})
                                                .jobLocationUrlFormatted && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(assignment.jobLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                            <span
                                                id="jobLocation"
                                                name="jobLocation"
                                            >
                                                {assignment.jobLocation
                                                    ?? '-'}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>

                                    {Boolean(assignment?.usCounty) && (
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="usCounty"
                                                label={t('confirm:county')}
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                <div>
                                                    <CountyLabel {...county} />
                                                </div>
                                            </FlexStartRow>
                                        </FormGroup>
                                    )}

                                    {!!jobUploadsEnabled
                                        && !!(assignment.jobUploads ?? [])
                                            .length && (
                                            <div className="mt-3">
                                                <FormGroup>
                                                    <FormLabel
                                                        label={t('confirm:jobAttachments')}
                                                        className="bottom-border"
                                                    />
                                                    <FileList
                                                        jobId={assignment.jobId}
                                                        files={assignment.jobUploads ?? []}
                                                    />
                                                </FormGroup>

                                            </div>
                                        )}
                                </div>

                                {!confirmed && showDriverDisclaimer && !this.state.isDisclaimerCheckedFromBackend && (
                                    <div className="disclamier">
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="hasAgreedToDriverDisclaimer"
                                                label={t('confirm:driverDisclaimerLabel')}
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                <FormCheckbox
                                                    id="hasAgreedToDriverDisclaimer"
                                                    name="hasAgreedToDriverDisclaimer"
                                                    onChange={this.onDisclaimerChange}
                                                    checked={this.state.hasAgreedToDriverDisclaimer}
                                                    disabled={this.state.disclaimerDisabled}
                                                    labelText={t('confirm:driverDisclaimerText')}
                                                />
                                            </FlexStartRow>
                                            {this.state.disclaimerError && (
                                                <small className="text-danger">{t('confirm:driverDisclaimerMustAgree')}</small>
                                            )}
                                        </FormGroup>
                                    </div>
                                )}

                                {(this.state.assignment ?? {})
                                    .confirmationStatusId !== 5 && (
                                        <div className="crewAndEquipmentDetails">
                                            <FormGroup>
                                                <FormLabel
                                                    label={t('confirm:crewAndEquipment')}
                                                    className="bottom-border"
                                                />
                                            </FormGroup>
                                            {!!confirmed
                                                && !!assignment.crewSMS && (
                                                    <FlexCenterRow>
                                                        <a
                                                            className="mb-2 p-1 btn btn-success btn-sm groupSmsLink text-white"
                                                            href={getGroupSmsUrl(assignment.crewSMS.split(','))}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faComments
                                                                }
                                                                size="lg"
                                                                className="mr-2"
                                                            />
                                                            {t('confirm:sendGroupSmsToCrew')}
                                                        </a>
                                                    </FlexCenterRow>
                                                )}

                                            <div className="crewAndEquipmentDetails table-responsive">
                                                <table className="table table-sm assignmentsTable">
                                                    <tbody>
                                                        {!!assignment.crew && (
                                                            <>
                                                                {assignment.crew.map(
                                                                    (c) => {
                                                                        const ownedAssignment = c.assignedEmployeeId
                                                                            === assignment.assignedEmployeeId;

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        owned: !!ownedAssignment,
                                                                                    },
                                                                                )}
                                                                                key={
                                                                                    c.id
                                                                                }
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                                'fa-hard-hat-golden':
                                                                                                    !!c.isCrewLead,
                                                                                            },
                                                                                        )}
                                                                                        icon={
                                                                                            c.isCrewLead
                                                                                                ? faHardHat
                                                                                                : faUser
                                                                                        }
                                                                                        title={
                                                                                            c.isCrewLead
                                                                                                ? crewLead
                                                                                                : c.assignedEmployeeGroup
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <FlexBetweenRow>
                                                                                        <small
                                                                                            className={cls(
                                                                                                {
                                                                                                    'text-success font-weight-bold':
                                                                                                        c.assignedEmployeeId
                                                                                                        === assignment.assignedEmployeeId,
                                                                                                },
                                                                                            )}
                                                                                        >
                                                                                            {c.isCrewLead
                                                                                                ? t('confirm:assignedEmployeeWithLead', { name: c.assignedEmployeeName, lead: crewLead })
                                                                                                : c.assignedEmployeeName}
                                                                                            {' '}
                                                                                            {parseInt(assignment.dispatchOfficeId) !== c.employeeDispatchCompany.id
                                                                                                ? t('confirm:dispatchedFrom', { name: c.employeeDispatchCompany.name })
                                                                                                : ''}
                                                                                        </small>
                                                                                        {!!confirmed
                                                                                            && !!c.assignedEmployeePhone && (
                                                                                                <a
                                                                                                    title={`Send SMS to ${c.assignedEmployeeName}`}
                                                                                                    className="ml-2 p-1 btn btn-sm btn-success"
                                                                                                    href={`sms://+1${c.assignedEmployeePhone}`}
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={
                                                                                                            faCommentDots
                                                                                                        }
                                                                                                        size="lg"
                                                                                                    />
                                                                                                </a>
                                                                                            )}
                                                                                    </FlexBetweenRow>
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed
                                                                                        && c.confirmationStatusId
                                                                                        === ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.5,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title={t('timesheet:employeeConfirmed')}
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    },
                                                                )}
                                                            </>
                                                        )}

                                                        {!!assignment.equipment && (
                                                            <>
                                                                {assignment.equipment.map(
                                                                    (e) => {
                                                                        const icon = e.assignedEquipmentIsVehicle ? faTruck : faToolbox;

                                                                        const ownedAssignment = e.equipmentEmployeeAssignmentId
                                                                            === assignment.assignedEmployeeId
                                                                            || dependentEquipmentIds.includes(
                                                                                e.assignedEquipmentId,
                                                                            );

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        owned: !!ownedAssignment,
                                                                                    },
                                                                                )}
                                                                                key={e.id}
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                            },
                                                                                        )}
                                                                                        icon={
                                                                                            icon
                                                                                        }
                                                                                    />
                                                                                </td>

                                                                                <td>
                                                                                    <FlexColumnStart>
                                                                                        <small className={cls({ 'text-success font-weight-bold': !!ownedAssignment })}>
                                                                                            {t('confirm:assignedEquipmentBase', { type: e.assignedEquipmentType, name: e.assignedEquipmentName })}
                                                                                            {e.quantity && e.quantity > 1 ? ` ${t('confirm:quantitySuffix', { quantity: e.quantity })}` : ''}
                                                                                            {parseInt(assignment.dispatchOfficeId) !== e.equipmentDispatchLocation.id
                                                                                                ? ` ${t('confirm:dispatchedFrom', { name: e.equipmentDispatchLocation.name })}`
                                                                                                : ''}
                                                                                        </small>

                                                                                        {e.equipmentEmployeeAssignmentName && (
                                                                                            <small>
                                                                                                {t('confirm:assignedTo', { name: e.equipmentEmployeeAssignmentName })}
                                                                                            </small>
                                                                                        )}

                                                                                        {e.equipmentAssignedEquipmentName && (
                                                                                            <small>
                                                                                                {t('confirm:assignedToEquipment', { type: e.equipmentAssignedEquipmentType, name: e.equipmentAssignedEquipmentName })}
                                                                                            </small>
                                                                                        )}

                                                                                        {Boolean(e.isRentalAssignment) && (
                                                                                            <>
                                                                                                {e.assignedEquipmentIsGeneric ? (
                                                                                                    <>
                                                                                                        {e.rentalDropoffQuantity > 0 && (
                                                                                                            <div>
                                                                                                                <small>
                                                                                                                    {t('confirm:dropoffWithQty', { quantity: e.rentalDropoffQuantity })}
                                                                                                                </small>
                                                                                                            </div>
                                                                                                        )}

                                                                                                        {e.rentalPickupQuantity > 0 && (
                                                                                                            <div>
                                                                                                                <small>
                                                                                                                    {t('confirm:pickupWithQty', { quantity: e.rentalPickupQuantity })}
                                                                                                                </small>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <div>
                                                                                                        <small>
                                                                                                            {e.isRentalPickup ? t('confirm:pickup') : t('confirm:dropoff')}
                                                                                                        </small>
                                                                                                    </div>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </FlexColumnStart>

                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed
                                                                                        && e.confirmationStatusId
                                                                                        === ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.7,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title={t('timesheet:equipmentConfirmed')}
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}

                                                                                    {!confirmed && e.confirmationStatusId !== ConfirmationStatus.Confirmed && ownedAssignment && (
                                                                                        <FontAwesomeIcon
                                                                                            size="lg"
                                                                                            className={cls('icon-check', 'checked')}
                                                                                            title={this.props.t('confirm:assignedToYouTooltip')}
                                                                                            icon={faCheckCircle}
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    },
                                                                )}
                                                            </>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                            </UncontrolledCollapseUnderlineHeader>
                        </>
                    )}
                </Container>

                <div className="userActions assignmentActions d-flex justify-content-center align-items-stretch p-1">
                    {(this.state.assignment ?? {}).confirmationStatusId
                        <= 3 && (
                            <StyledButton
                                color="success"
                                disabled={saving}
                                onClick={() => this.confirmAssignment()}
                                startIcon={saving ? faCircleNotch : faCheckCircle}
                                StartIconProps={{ size: 'lg' }}
                            >
                                {t('confirm:confirm')}
                            </StyledButton>
                        )}
                    {(this.state.assignment ?? {}).confirmationStatusId
                        === 5 && (
                            <StyledButton
                                color="danger"
                                disabled={saving}
                                onClick={() => this.confirmCancelledAssignment()}
                                startIcon={saving ? faCircleNotch : faCheckCircle}
                                StartIconProps={{ size: 'lg' }}
                            >
                                {t('confirm:confirmCancellation')}
                            </StyledButton>
                        )}

                    {!!this.state.assignment?.timesheetId && (
                        <Link
                            component={StyledButton}
                            color="success"
                            startIcon={faUserClock}
                            StartIconProps={{ size: 'lg' }}
                            to={assignment?.isCrewLead ? `${AppNavPaths.Timesheet}/${this.state.assignment.timesheetId}/${this.state.assignment.jobAssignmentId}` : `${AppNavPaths.CrewTimesheet}/${assignment.timesheetId}/${assignment.assignedEmployeeId}`}
                        >
                            {t('confirm:goToTimesheet')}
                        </Link>
                    )}

                    {
                        // Employee call off is switched off for now. Uncomment to show the Call Off button again.
                        // {Boolean(assignment?.canCallOff) && (
                        //     <CallOffButton
                        //         className="ml-2 d-flex align-items-center justify-content-center"
                        //         disabled={saving}
                        //         jobAssignmentId={assignment.jobAssignmentId}
                        //         onSuccess={() => this.context.history.push(AppNavPaths.AssignmentCalendar)}
                        //     />
                        // )}
                    }
                    {this.state?.assignment?.dispatchOfficeNumber && (
                        <StyledButton
                            color="dark"
                            className="officeCallAction ml-2"
                            href={`tel:${this.state?.assignment?.dispatchOfficeNumber}`}
                            startIcon={faPhoneSquareAlt}
                            StartIconProps={{ size: 'lg' }}
                        >
                            {t('confirm:officeWithNumber', { number: this.state?.assignment?.dispatchOfficeNumber })}
                        </StyledButton>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(AssignmentConfirmation);
